import { ParsedUrlQuery } from 'querystring'

import { Colors } from '@kvika/audur-theme'
import { SliceZone } from '@prismicio/react'
import { GetStaticProps } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMediaQuery } from 'react-responsive'

import { GridItem, MainGridContainer } from '../../components/grid-container'
import InterestRateCBChart from '../../components/slices/InterestRateCBChart/InterestRateCBChart'
import PricingTable from '../../components/slices/PricingTable'
import Layout from '../../container/layout/Layout'
import MortgageFAQSlice from '../../container/mortgage/mortgageFAQSlice'
import MortgageHeroCTASlice from '../../container/mortgage/mortgageHeroCTASlice'
import AudurPrismicClient from '../../prismic/prismicClient'
import {
	AudurFooter,
	AudurHeader,
	Maybe,
	MortgageSubPage,
	MortgageSubPageConnectionConnection,
	MortgageSubPageConnectionEdge,
} from '../../prismic/prismicTypes'
import { valueOrDefault } from '../../prismic/prismicUtils'
import { prismicConstants, SITE_ALERT_REVALIDATE_SECONDS } from '../../utils/Constants'
import { renderEditorialItemGroup } from '../../utils/editorial/renderEditorial'
import { Route } from '../../utils/navigation/Routes'
import { TranslatedStrings } from '../../utils/translations/TranslatedStrings'
import { ColumnClass, MetaTagInfo, PageLayout, PreviewData, SiteAlert } from '../../utils/types/Types'
import { getAudurFooter, getAudurHeader, getAudurSiteAlert, getMetaTags } from '../../utils/Utils'

import styles from './index.module.scss'

type IMortgageSubPageProps = {
	footer: AudurFooter
	header: AudurHeader
	siteAlert: SiteAlert
	metaTags: MetaTagInfo
	mortgageSubPage: Maybe<MortgageSubPage>
}

/**
 * Gets the correct breadcrumb text based on the current path
 */
const getBreadcrumbText = (path: string): string => {
	const { mortgagePage } = TranslatedStrings.is

	// Map route endings to their corresponding text in TranslatedStrings
	if (path.endsWith(Route.MortgageEquity)) {
		return mortgagePage.mortgageEquityTitle
	}

	if (path.endsWith(Route.MortgageEqualization)) {
		return mortgagePage.mortgageEqualizationTitle
	}

	if (path.endsWith(Route.MortgagePricing)) {
		return mortgagePage.mortgagePricingTitle
	}

	if (path.endsWith(Route.MortgageCBInterestRate)) {
		return mortgagePage.mortgageCBInterestRateTitle
	}

	// Extract the last part of the path for any unmapped subpages
	const pathSegments = path.split('/')
	return pathSegments[pathSegments.length - 1] || ''
}

const MortgageSubpage = ({ footer, header, siteAlert, mortgageSubPage, metaTags }: IMortgageSubPageProps) => {
	const router = useRouter()
	const { mortgagePage } = TranslatedStrings.is
	const isMobile = useMediaQuery({ query: '(max-width: 599px)' })
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 979px)' })

	return (
		<Layout
			title={mortgagePage.mortgagePricingTitle}
			metaTags={metaTags}
			footer={footer}
			header={header}
			siteAlert={siteAlert}
		>
			<div className={styles.standardContentContainer}>
				<MainGridContainer backgroundColor={Colors.Transparent}>
					<GridItem className={ColumnClass.All}>
						<div className={styles.breadCrumbs}>
							{!isTabletOrMobile && (
								<Image
									src='/svg/arrow_left.svg'
									alt='arrow_left'
									width={17.5}
									height={15}
									onClick={() => {
										router.back()
									}}
									style={{ cursor: 'pointer' }}
								/>
							)}
							<Link href={Route.Mortgage} legacyBehavior>
								<a className={styles.linkButton}>{TranslatedStrings.is.mortgagePage.heroDefaultTitle}</a>
							</Link>
							<div className={styles.rectangle} />
							<Link href={router.asPath} legacyBehavior>
								<a className={styles.linkButton}>{getBreadcrumbText(router.asPath)}</a>
							</Link>
						</div>
					</GridItem>
				</MainGridContainer>
				{mortgageSubPage?.editorialContentGroup?.map((group, index) => {
					return renderEditorialItemGroup(group, PageLayout.Standard, index, isMobile, isTabletOrMobile)
				})}
				{mortgageSubPage?.body && (
					<SliceZone
						slices={mortgageSubPage.body.map((slice) => ({ ...slice, type: valueOrDefault(slice.type, '') }))}
						components={{
							herowithcta: ({ slice }) => {
								return (
									<MortgageHeroCTASlice
										{...slice}
										hideImageMobile
										firstButtonLink={`${Route.ApplyForMortgage}?redirect=application-flow`}
										secondButtonLink={Route.MortgageCalculator}
										isSubpage
										hasFAQ={!!mortgageSubPage.body?.find((s) => s.type === 'calculator_faq')}
									/>
								)
							},
							calculator_faq: ({ slice }) => {
								return <MortgageFAQSlice {...slice} />
							},
							pricingtable: ({ slice }) => {
								return <PricingTable title={slice?.primary?.pricingTitle || ''} tableFields={slice?.fields || []} />
							},
							'interest-rate-cb': ({ slice }) => {
								return (
									<InterestRateCBChart
										chartTitle={slice?.primary?.interestrate_cb_chartTitle || ''}
										description={slice?.primary?.interestrate_cb_description || ''}
										chartLine={slice?.primary?.interestrate_cb_chartLine || ''}
									/>
								)
							},
						}}
					/>
				)}
			</div>
		</Layout>
	)
}

export default MortgageSubpage

interface Params extends ParsedUrlQuery {
	uid: string
}

export const getStaticProps: GetStaticProps<IMortgageSubPageProps, Params> = async ({ params, previewData }) => {
	const uId = params ? params.uid : ''
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const mortgageSubPage = await audurClient.getMortgageSubPage({
		previewData: previewData as PreviewData,
		variables: { uId, lang: prismicConstants.apiDefaultLocale },
	})

	const [footer, header, siteAlert] = await Promise.all([getAudurFooter(), getAudurHeader(), getAudurSiteAlert()])

	const metaTags = getMetaTags({
		...mortgageSubPage,
		__typename: 'Mortgage_sub_page',
	} as Maybe<MortgageSubPage>)

	return {
		props: {
			mortgageSubPage,
			metaTags,
			footer,
			header,
			siteAlert,
		},
		revalidate: SITE_ALERT_REVALIDATE_SECONDS,
	}
}

export async function getStaticPaths() {
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const maybeMortgageSubPageConnection: Maybe<MortgageSubPageConnectionConnection> =
		await audurClient.getAllMortgageSubPages({
			variables: {
				lang: 'is',
			},
		})
	const edges = maybeMortgageSubPageConnection ? maybeMortgageSubPageConnection.edges : []
	const paths =
		edges?.map((maybeEdge: Maybe<MortgageSubPageConnectionEdge>) => {
			return maybeEdge
				? {
						params: {
							uid: `${maybeEdge.node._meta.uid}`,
							lang: `${maybeEdge.node._meta.lang}`,
						},
					}
				: { params: {} }
		}) || []

	return {
		paths,
		fallback: false,
	}
}
